import { MAX_TRIP_MESSAGE_LENGTH } from '@chukua/common';
import { Alert, Button, Input } from 'antd';
import { MessageSquare, ShieldAlert } from 'lucide-react';
import { useState } from 'react';
import { EmptyState } from '../../../../../shared/components/EmptyState';
import { RowListSkeleton } from '../../../../../shared/components/RowListSkeleton';
import { useTripMessages } from '../hooks/use-trip-messages';
import type { TripMessageView } from '../trip-messages.api';

function MessageRow({ message }: { message: TripMessageView }) {
  const fromDriver = message.authorKind === 'driver';
  return (
    <li className={`flex flex-col gap-1 rounded-lg border border-line px-3 py-2 ${fromDriver ? 'bg-surface' : 'bg-primary/10'}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-2 text-xs text-muted">
        <span>{message.authorName ?? (fromDriver ? 'Driver' : 'Office')}</span>
        <time dateTime={message.createdAt}>{new Date(message.createdAt).toLocaleString()}</time>
      </div>
      {message.body && <p className="text-sm break-words whitespace-pre-wrap">{message.body}</p>}
      {message.photos.length > 0 && <p className="text-xs text-muted">{message.photos.length} photo(s) attached</p>}
    </li>
  );
}

/** The thread between the office and the trip's driver. Sending needs `trip.communicate`; without it the thread is read-only. */
export function TripMessagesPanel({ tripId, canSend }: { tripId: string; canSend: boolean }) {
  const { state, retry, send, isSending, error } = useTripMessages(tripId);
  const [draft, setDraft] = useState('');

  if (state.status === 'loading') return <RowListSkeleton label="Loading messages" />;
  if (state.status === 'error') {
    return (
      <EmptyState icon={ShieldAlert} title="Couldn't load the messages" description="Something went wrong reading this trip's messages.">
        <Button onClick={() => void retry()}>Try again</Button>
      </EmptyState>
    );
  }

  async function submit() {
    if (await send(draft.trim())) setDraft('');
  }

  return (
    <div className="flex flex-col gap-4">
      {state.messages.length === 0 ? (
        <EmptyState icon={MessageSquare} title="No messages yet" description="Messages between the office and this trip's driver appear here." />
      ) : (
        <ul aria-label="Messages" className="flex flex-col gap-2">
          {state.messages.map((message) => (
            <MessageRow key={message.id} message={message} />
          ))}
        </ul>
      )}
      {canSend && (
        <form
          className="flex flex-col gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            void submit();
          }}
        >
          {error && <Alert type="error" showIcon title={error} />}
          <Input.TextArea
            aria-label="Message to the driver"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            rows={3}
            maxLength={MAX_TRIP_MESSAGE_LENGTH}
          />
          <Button type="primary" htmlType="submit" loading={isSending} disabled={draft.trim() === ''} className="self-end">
            Send message
          </Button>
        </form>
      )}
    </div>
  );
}
